import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import DiscardChangesModal from '@components/v2/ui/DiscardChangesModal/DiscardChangesModal';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Divider, Grid } from '@mui/material';
import { useCallback } from 'react';
import SortableContext from '../../components/DragAndDrop/SortableContext';
import { SortableStrategy } from '../../components/DragAndDrop/dragAndDrop.types';
import GroupedCarousel from '../../components/Site/GroupedCarousel';
import SortableSiteCatalogCard from '../../components/Site/SortableSiteCatalogCard';
import EmptyPlaceholder from '../../components/Table/EmptyPlaceholder';
import useMediaStyles from '../../components/useMediaStyles';
import LangEnum from '../../lang/LangEnum';
import { EMPTY_MEDIAS_PATH } from '../../utils/images.constants';
import { Catalog, EmphasisCatalog } from '../catalogs/catalog.types';
import CatalogCardBase from '../catalogs/pages/CatalogListPage/components/CatalogCard/CatalogCardBase';
import { useTranslation } from '../translation/TranslationContext';
import useEmphasisCatalogEdit from './hooks/useEmphasisCatalogEdit';

const CATALOG_CARD_COLUMNS = { xs: 6, sm: 4, md: 3 };

const CATALOG_CAROUSEL_GROUP_SIZE = 8;

type EmphasisCatalogEditProps = {
  isOpen: boolean;
  isSaving: boolean;
  catalogs: Catalog[];
  emphasisCatalogs: EmphasisCatalog[];
  onSave: (emphasisCatalog: EmphasisCatalog[]) => void;
  onClose: () => void;
};

const EmphasisCatalogEdit = ({
  isOpen,
  isSaving,
  catalogs,
  emphasisCatalogs,
  onSave,
  onClose,
}: EmphasisCatalogEditProps) => {
  const { translate } = useTranslation();
  const { classes: mediaClasses } = useMediaStyles({});

  const {
    selectedCatalogs,
    selectedCatalogIds,
    discardModalProps,
    setSelectedCatalogs,
    toggleSelectCatalog,
    handleRemove,
    handleExited,
    handleRequestClose,
    save,
  } = useEmphasisCatalogEdit({ isSaving, emphasisCatalogs, onSave, onClose });

  const renderCatalogToSelect = useCallback(
    (items: Catalog[]) => (
      <Grid container spacing={1.5}>
        {items.map((item) => {
          const isSelected = selectedCatalogIds.includes(item.id);
          return (
            <Grid key={item.id} size={CATALOG_CARD_COLUMNS}>
              <Box className={mediaClasses.root}>
                <CatalogCardBase catalog={item} />
                <Box className={mediaClasses.mediaControlsTop}>
                  <Checkbox
                    className={isSelected ? undefined : 'ActionButton'}
                    checked={isSelected}
                    onChange={() => toggleSelectCatalog(item, isSelected)}
                  />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    ),
    [selectedCatalogIds, toggleSelectCatalog, mediaClasses],
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="registration"
        header={{ title: LangEnum.SHOWROOM_FEATURED_CATALOGS_SELECTED }}
        onClose={handleRequestClose}
        onExited={handleExited}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_SAVE,
            isLoading: isSaving,
            onClick: save,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: handleRequestClose,
          },
        }}
      >
        <Grid container>
          <Grid size={12}>
            <Box
              sx={{
                pb: 2,
              }}
            >
              <Typography>{translate(LangEnum.SHOWROOM_FEATURED_CATALOGS)}</Typography>
            </Box>
            <SortableContext
              items={selectedCatalogs}
              onUpdateItems={setSelectedCatalogs}
              uniqueIdentifier="id"
              strategy={SortableStrategy.GRID}
              OverlayComponent={({ activeIndex }) => (
                <SortableSiteCatalogCard isDragged item={selectedCatalogs[activeIndex]} />
              )}
            >
              <Grid
                container
                spacing={1.5}
                sx={{
                  alignItems: 'stretch',
                }}
              >
                {selectedCatalogs.map((catalog: EmphasisCatalog) => (
                  <Grid key={catalog.id} id={catalog.id} size={CATALOG_CARD_COLUMNS}>
                    <SortableSiteCatalogCard
                      item={catalog}
                      isDragged={false}
                      onRemove={handleRemove}
                    />
                  </Grid>
                ))}
              </Grid>
            </SortableContext>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          {!catalogs || catalogs.length === 0 ? (
            <Grid size={12}>
              <EmptyPlaceholder
                src={EMPTY_MEDIAS_PATH}
                message={translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_PLACEHOLDER)}
              />
            </Grid>
          ) : (
            <Grid size={12}>
              <Box
                sx={{
                  py: 2,
                }}
              >
                <Typography>
                  {translate(LangEnum.SHOWROOM_FEATURED_CATALOGS_SELECT_MORE)}
                </Typography>
              </Box>
              <GroupedCarousel
                items={catalogs}
                groupSize={CATALOG_CAROUSEL_GROUP_SIZE}
                navigation="overlay"
                renderItemGroup={renderCatalogToSelect}
              />
            </Grid>
          )}
        </Grid>
      </Modal>

      <DiscardChangesModal {...discardModalProps} />
    </>
  );
};

export default EmphasisCatalogEdit;
